import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Loader2, Trash2, Upload } from 'lucide-react';
import api from '../../services/api';

const absolute = (u) => (u && u.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const copy = (text) => {
  navigator.clipboard.writeText(text).then(() => toast.success('Link copied'), () => toast.error('Copy failed'));
};

// Full-HTML editor for the "html" section kind (scripts allowed; rendered as-is on the site).
export function HtmlField({ value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">HTML</label>
      <textarea rows={14} value={value} onChange={(e) => onChange(e.target.value)} spellCheck={false}
        className="w-full input font-mono text-xs leading-relaxed" placeholder="<div class='...'>…</div>" data-testid={testid} />
      <p className="text-[11px] text-slate-500 mt-1">
        Full HTML, inline styles and scripts run as-is on the public site. Use image links from the Media Library tab.
      </p>
    </div>
  );
}

const MediaCard = ({ m, onDelete }) => {
  const link = absolute(m.url);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={`media-item-${m.id}`}>
      <div className="aspect-video bg-slate-50 flex items-center justify-center">
        <img src={link} alt={m.original_filename} className="max-h-full max-w-full object-contain" loading="lazy" />
      </div>
      <div className="p-2 space-y-1.5">
        <p className="text-xs text-slate-700 truncate" title={m.original_filename}>{m.original_filename}</p>
        <div className="flex gap-1.5">
          <button type="button" onClick={() => copy(link)} className="flex-1 text-xs px-2 py-1 border border-slate-300 rounded inline-flex items-center justify-center gap-1 hover:bg-slate-50" data-testid={`media-copy-${m.id}`}>
            <Copy className="w-3 h-3" />Copy link
          </button>
          <button type="button" onClick={() => onDelete(m)} className="text-xs px-2 py-1 text-rose-600 border border-rose-200 rounded hover:bg-rose-50" data-testid={`media-delete-${m.id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export function MediaLibraryPanel() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => api.get('/super-admin/website/media').then((r) => setItems(r.data.media || [])).catch(() => toast.error('Failed to load media')), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name}: too large (max 5 MB)`); continue; }
      const fd = new FormData();
      fd.append('file', f);
      try {
        await api.post('/super-admin/website/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) { toast.error(`${f.name}: ${err?.response?.data?.detail || 'upload failed'}`); }
    }
    setUploading(false);
    e.target.value = '';
    toast.success('Upload finished');
    load();
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.original_filename}? Pages using its link will show a broken image.`)) return;
    try { await api.delete(`/super-admin/website/media/${m.id}`); load(); } catch (_e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="media-library-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 cursor-pointer px-4 py-2 rounded" data-testid="media-upload-label">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading…' : 'Upload images'}
          <input type="file" multiple accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" onChange={upload} disabled={uploading} className="hidden" data-testid="media-upload-input" />
        </label>
        <span className="text-xs text-slate-500">PNG, JPG, GIF, WEBP or SVG up to 5 MB. Copy a link and use it in any section, HTML block or logo field.</span>
      </div>
      {items === null ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="media-empty">No images yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {items.map((m) => <MediaCard key={m.id} m={m} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
